import { useMemo } from "react";
import { CheckCircle2, Clock, LockKeyhole, PackageCheck } from "lucide-react";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { ModuloSaas, useModulosSaas } from "@/hooks/useModulosSaas";

const money = (value?: number) =>
  Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const statusBadge = (status?: string) => {
  if (status === "Ativo" || status === "Teste") return "default";
  if (status === "Bloqueado" || status === "Cancelado") return "destructive";
  return "secondary";
};

const actionLabel = (modulo: ModuloSaas) => {
  if (modulo.regra_cobranca === "gratuito") return "Ativar grátis";
  if (modulo.regra_cobranca === "teste") return "Solicitar teste";
  if (modulo.regra_cobranca === "mensal") return "Solicitar contratação";
  return "Solicitar liberação";
};

export function ModulosCliente() {
  const { toast } = useToast();
  const { modulosAtivos, getLiberacao, upsertLiberacao, loading, clienteId } = useModulosSaas();

  const grouped = useMemo(() => {
    return modulosAtivos.reduce<Record<string, ModuloSaas[]>>((acc, modulo) => {
      const categoria = modulo.categoria || "Geral";
      acc[categoria] = acc[categoria] || [];
      acc[categoria].push(modulo);
      return acc;
    }, {});
  }, [modulosAtivos]);

  const solicitar = async (modulo: ModuloSaas) => {
    const status = modulo.regra_cobranca === "gratuito" ? "Ativo" : "Solicitado";
    await upsertLiberacao({
      modulo_chave: modulo.chave,
      cliente_id: clienteId,
      status,
      escopo_aplicado: modulo.escopo,
      valor_mensal: Number(modulo.valor_mensal || 0),
    });
    toast({
      title: status === "Ativo" ? "Módulo ativado" : "Solicitação enviada",
      description: status === "Ativo" ? "O módulo gratuito já está liberado para a conta." : "O superadmin poderá aprovar a liberação.",
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold">Módulos</h1>
          <p className="text-sm text-muted-foreground">Central de recursos disponíveis para a conta SaaS.</p>
        </div>

        {loading ? (
          <Card>
            <CardContent className="p-6 text-sm text-muted-foreground">Carregando módulos...</CardContent>
          </Card>
        ) : modulosAtivos.length === 0 ? (
          <Card>
            <CardContent className="space-y-2 p-6">
              <p className="font-medium">Nenhum módulo publicado ainda.</p>
              <p className="text-sm text-muted-foreground">O superadmin precisa cadastrar os módulos comerciais antes de aparecerem aqui.</p>
            </CardContent>
          </Card>
        ) : (
          Object.entries(grouped).map(([categoria, items]) => (
            <div key={categoria} className="space-y-3">
              <h2 className="text-sm font-medium text-muted-foreground">{categoria}</h2>
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {items.map((modulo) => {
                  const liberacao = getLiberacao(modulo.chave);
                  const active = liberacao?.status === "Ativo" || liberacao?.status === "Teste" || modulo.regra_cobranca === "gratuito";
                  const requested = liberacao?.status === "Solicitado";
                  return (
                    <Card key={modulo.id}>
                      <CardHeader className="space-y-3">
                        <div className="flex items-start justify-between gap-3">
                          <CardTitle className="text-base">{modulo.nome}</CardTitle>
                          <Badge variant={statusBadge(active ? "Ativo" : liberacao?.status)}>
                            {active ? "Ativo" : liberacao?.status || "Disponível"}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground">{modulo.descricao || "Módulo operacional da plataforma."}</p>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="flex items-center justify-between rounded-md bg-muted/40 px-3 py-2 text-sm">
                          <span>{modulo.regra_cobranca === "gratuito" ? "Gratuito" : "Mensalidade"}</span>
                          <strong>{modulo.regra_cobranca === "gratuito" ? "Incluso" : money(modulo.valor_mensal)}</strong>
                        </div>
                        <Button className="w-full" variant={active ? "outline" : "default"} disabled={active || requested} onClick={() => solicitar(modulo)}>
                          {active ? <CheckCircle2 className="mr-2 h-4 w-4" /> : requested ? <Clock className="mr-2 h-4 w-4" /> : <PackageCheck className="mr-2 h-4 w-4" />}
                          {active ? "Liberado" : requested ? "Aguardando aprovação" : actionLabel(modulo)}
                        </Button>
                        {modulo.bloqueio_ativo && !active && (
                          <p className="flex items-center gap-2 text-xs text-muted-foreground">
                            <LockKeyhole className="h-3 w-3" />
                            Este módulo só aparece no menu depois da liberação.
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>
    </DashboardLayout>
  );
}
